import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useModules } from '../../contexts/ModulesContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import JsBarcode from 'jsbarcode';

const API = process.env.REACT_APP_BACKEND_URL;

export const BackshopExportView = () => {
  const { exportId } = useParams();
  const navigate = useNavigate();
  const { isModuleEnabled } = useModules();
  const [loading, setLoading] = useState(true);
  const [exportData, setExportData] = useState(null);
  const [productData, setProductData] = useState([]);
  const barcodeRefs = useRef({});

  useEffect(() => {
    fetchExportData();
  }, [exportId]);

  useEffect(() => {
    // Generate barcodes after product data is loaded
    if (productData.length > 0) {
      generateBarcodes();
    }
  }, [productData]);

  const fetchExportData = async () => {
    try {
      const response = await axios.get(`${API}/api/backshop/exports/${exportId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = response.data;
      setExportData(data);
      
      // Process product data based on format
      if (data.format === 'summary') {
        // For summary, aggregate products
        const products = {};
        
        data.writeoffs.forEach(wo => {
          const pid = wo.product_id;
          if (!products[pid]) {
            products[pid] = {
              id: pid,
              name: wo.product_name,
              article_number: wo.article_number || '-',
              quantity: 0,
              type: wo.type
            };
          }
          products[pid].quantity += wo.quantity;
        });
        
        // Separate by type
        const backshopProducts = Object.values(products).filter(p => p.type === 'backshop');
        const tgtgProducts = Object.values(products).filter(p => p.type === 'tgtg');
        
        setProductData([
          { type: 'backshop', products: backshopProducts },
          { type: 'tgtg', products: tgtgProducts }
        ]);
      } else {
        // For detailed, just pass through
        setProductData(data.writeoffs);
      }
      
    } catch (error) {
      console.error('Failed to fetch export:', error);
      toast.error('Fehler beim Laden des Exports');
    } finally {
      setLoading(false);
    }
  };

  const generateBarcodes = () => {
    // Generate barcodes for all products
    Object.keys(barcodeRefs.current).forEach(key => {
      const canvas = barcodeRefs.current[key];
      if (canvas && canvas.dataset.articleNumber && canvas.dataset.articleNumber !== '-') {
        try {
          JsBarcode(canvas, canvas.dataset.articleNumber, {
            format: 'CODE128',
            width: 2,
            height: 50,
            displayValue: false, // Don't show text below barcode (we display it separately)
            margin: 5,
            background: '#ffffff',
            lineColor: '#000000',
          });
        } catch (error) {
          console.error(`Error generating barcode for ${canvas.dataset.articleNumber}:`, error);
        }
      }
    });
  };

  const downloadPDF = async () => {
    try {
      toast.info('PDF wird vorbereitet...');
      
      const response = await axios.get(
        `${API}/api/backshop/exports/${exportId}/pdf`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          responseType: 'blob',
          timeout: 30000
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Export-${exportId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF erfolgreich heruntergeladen!');
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Fehler beim Herunterladen des PDFs');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Laden...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!exportData) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-red-600">Export nicht gefunden</p>
          <Button onClick={() => navigate('/admin/abschriften/backshop-export')} className="mt-4">
            Zurück zur Übersicht
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const typeMap = {
    'backshop': 'Backshop',
    'tgtg': 'TGTG',
    'both': 'Backshop + TGTG'
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-6">
        {/* Header - Hide on print */}
        <div className="print:hidden flex items-center justify-between">
          <div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
              Export-Ansicht
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Scannen Sie die Barcodes mit Ihrem Scanner
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Drucken
            </Button>
            <Button onClick={downloadPDF} className="bg-orange-600 hover:bg-orange-700">
              <Download className="h-4 w-4 mr-2" />
              PDF laden
            </Button>
          </div>
        </div>

        {/* Export Info */}
        <Card className="print:shadow-none">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white print:bg-white dark:bg-gray-900 print:text-black">
            <CardTitle>
              Abschrift-Export: {typeMap[exportData.type]}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-2">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Export-ID:</span> {exportData.id}
              </div>
              <div>
                <span className="font-medium">Zeitraum:</span>{' '}
                {new Date(exportData.start_date).toLocaleDateString('de-DE')} -{' '}
                {new Date(exportData.end_date).toLocaleDateString('de-DE')}
              </div>
              <div>
                <span className="font-medium">Format:</span>{' '}
                {exportData.format === 'summary' ? 'Zusammenfassung' : 'Detailliert'}
              </div>
              <div>
                <span className="font-medium">Erstellt am:</span>{' '}
                {new Date(exportData.created_at).toLocaleString('de-DE')}
              </div>
            </div>
            <div className="pt-4 border-t">
              <div className="flex gap-6 text-sm">
                <div>
                  <span className="font-medium">Gesamt-Produkte:</span>{' '}
                  <Badge variant="outline">{exportData.total_products}</Badge>
                </div>
                <div>
                  <span className="font-medium">Gesamt-Menge:</span>{' '}
                  <Badge variant="outline">{exportData.total_quantity} Stück</Badge>
                </div>
                {exportData.type === 'both' && (
                  <>
                    <div>
                      <span className="font-medium">Backshop:</span>{' '}
                      <Badge variant="outline" className="bg-orange-50 text-orange-700">
                        {exportData.backshop_quantity} Stück
                      </Badge>
                    </div>
                    <div>
                      <span className="font-medium">TGTG:</span>{' '}
                      <Badge variant="outline" className="bg-green-50 text-green-700">
                        {exportData.tgtg_quantity} Stück
                      </Badge>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Product Tables */}
        {exportData.format === 'summary' ? (
          <>
            {/* BACKSHOP Section */}
            {productData.find(g => g.type === 'backshop')?.products.length > 0 && (
              <Card>
                <CardHeader className="bg-orange-100 print:bg-white dark:bg-gray-900">
                  <CardTitle className="text-orange-700 print:text-black">BACKSHOP ABSCHRIFTEN</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-orange-600 text-white print:bg-gray-200 print:text-black">
                        <th className="text-left p-3 font-semibold">Produkt-Name</th>
                        <th className="text-left p-3 font-semibold">Artikel-Nr.</th>
                        <th className="text-center p-3 font-semibold">Barcode</th>
                        <th className="text-right p-3 font-semibold">Gesamt-Menge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productData.find(g => g.type === 'backshop')?.products
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((product, idx) => (
                        <tr key={product.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="p-3">{product.name}</td>
                          <td className="p-3 text-sm text-gray-600 dark:text-gray-400">{product.article_number}</td>
                          <td className="p-3 text-center">
                            {product.article_number && product.article_number !== '-' ? (
                              <canvas
                                ref={el => {
                                  if (el) {
                                    barcodeRefs.current[`backshop-${product.id}`] = el;
                                    el.dataset.articleNumber = product.article_number;
                                  }
                                }}
                                className="mx-auto"
                              />
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="p-3 text-right font-semibold">{product.quantity} Stück</td>
                        </tr>
                      ))}
                      <tr className="bg-orange-200 print:bg-gray-300 font-bold">
                        <td colSpan="3" className="p-3 text-right">Gesamt Backshop:</td>
                        <td className="p-3 text-right text-lg">
                          {productData.find(g => g.type === 'backshop')?.products.reduce((sum, p) => sum + p.quantity, 0)} Stück
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* TGTG Section - Only show if Backshop module is enabled (TGTG is part of Backshop) */}
            {isModuleEnabled('backshop') && productData.find(g => g.type === 'tgtg')?.products.length > 0 && (
              <Card className="mt-6">
                <CardHeader className="bg-green-100 print:bg-white dark:bg-gray-900">
                  <CardTitle className="text-green-700 print:text-black">TGTG ABSCHRIFTEN</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-green-600 text-white print:bg-gray-200 print:text-black">
                        <th className="text-left p-3 font-semibold">Produkt-Name</th>
                        <th className="text-left p-3 font-semibold">Artikel-Nr.</th>
                        <th className="text-center p-3 font-semibold">Barcode</th>
                        <th className="text-right p-3 font-semibold">Gesamt-Menge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productData.find(g => g.type === 'tgtg')?.products
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((product, idx) => (
                        <tr key={product.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="p-3">{product.name}</td>
                          <td className="p-3 text-sm text-gray-600 dark:text-gray-400">{product.article_number}</td>
                          <td className="p-3 text-center">
                            {product.article_number && product.article_number !== '-' ? (
                              <canvas
                                ref={el => {
                                  if (el) {
                                    barcodeRefs.current[`tgtg-${product.id}`] = el;
                                    el.dataset.articleNumber = product.article_number;
                                  }
                                }}
                                className="mx-auto"
                              />
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="p-3 text-right font-semibold">{product.quantity} Stück</td>
                        </tr>
                      ))}
                      <tr className="bg-green-200 print:bg-gray-300 font-bold">
                        <td colSpan="3" className="p-3 text-right">Gesamt TGTG:</td>
                        <td className="p-3 text-right text-lg">
                          {productData.find(g => g.type === 'tgtg')?.products.reduce((sum, p) => sum + p.quantity, 0)} Stück
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Grand Total */}
            {exportData.type === 'both' && (
              <Card className="bg-blue-50 print:bg-white dark:bg-gray-900 border-2 border-blue-300">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center text-xl font-bold">
                    <span>GESAMT (Backshop + TGTG):</span>
                    <span className="text-blue-700 print:text-black">{exportData.total_quantity} Stück</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          /* Detailed View */
          <Card>
            <CardHeader>
              <CardTitle>Detaillierte Abschriftenliste</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="bg-orange-600 text-white print:bg-gray-200 print:text-black">
                    <th className="text-left p-3">Datum</th>
                    <th className="text-left p-3">Mitarbeiter</th>
                    <th className="text-left p-3">Produkt</th>
                    <th className="text-left p-3">Art.-Nr.</th>
                    <th className="text-center p-3">Barcode</th>
                    <th className="text-right p-3">Menge</th>
                    <th className="text-center p-3">Typ</th>
                  </tr>
                </thead>
                <tbody>
                  {productData.map((wo, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 text-sm">{wo.timestamp?.slice(0, 10)}</td>
                      <td className="p-3 text-sm">{wo.employee_name}</td>
                      <td className="p-3">{wo.product_name}</td>
                      <td className="p-3 text-sm text-gray-600 dark:text-gray-400">{wo.article_number || '-'}</td>
                      <td className="p-3 text-center">
                        {wo.article_number && wo.article_number !== '-' ? (
                          <canvas
                            ref={el => {
                              if (el) {
                                barcodeRefs.current[`detail-${idx}`] = el;
                                el.dataset.articleNumber = wo.article_number;
                              }
                            }}
                            className="mx-auto"
                          />
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="p-3 text-right font-semibold">{wo.quantity}</td>
                      <td className="p-3 text-center">
                        <Badge variant={wo.type === 'backshop' ? 'default' : 'secondary'}>
                          {wo.type === 'backshop' ? 'BS' : 'TGTG'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:bg-white {
            background-color: white !important;
          }
          .print\\:bg-gray-200 {
            background-color: #e5e7eb !important;
          }
          .print\\:bg-gray-300 {
            background-color: #d1d5db !important;
          }
          .print\\:text-black {
            color: black !important;
          }
          @page {
            size: A4;
            margin: 1cm;
          }
        }
      `}</style>
    </DashboardLayout>
  );
};
